/**
 * MIME type detection and filename generation utilities for voice recordings.
 *
 * These helpers are used by AudioRecorder to select the best supported
 * audio format and generate properly-named output files.
 */

import { PREFERRED_AUDIO_TYPES } from "./constants";

/**
 * Returns the first MIME type from {@link PREFERRED_AUDIO_TYPES} that the
 * browser's MediaRecorder supports.
 *
 * @returns The best supported MIME type string, or `""` if none match
 *          (which lets the browser pick its own default format).
 */
export function getBrowserSupportedMimeType(): string {
  if (typeof MediaRecorder === "undefined") {
    return "";
  }
  for (const format of PREFERRED_AUDIO_TYPES) {
    if (MediaRecorder.isTypeSupported(format)) {
      return format;
    }
  }
  return "";
}

/**
 * Derives a file extension from a MIME type string.
 * Falls back to `"mp4"` for unknown types (Safari default).
 *
 * @param mime - The MIME type string (e.g. `"audio/webm;codecs=opus"`).
 * @returns A file extension without the leading dot (e.g. `"webm"`, `"mp4"`, `"ogg"`).
 */
export function extensionFromMime(mime: string): string {
  if (mime.includes("webm")) {
    return "webm";
  }
  if (mime.includes("mp4") || mime.includes("aac")) {
    return "mp4";
  }
  if (mime.includes("ogg")) {
    return "ogg";
  }
  return "mp4";
}

/**
 * Generates a timestamped filename for a voice recording.
 *
 * @param mime - The MIME type used during recording.
 * @returns A filename like `"voice-1709330400000.webm"`.
 */
export function generateVoiceFilename(mime: string): string {
  return `voice-${Date.now()}.${extensionFromMime(mime)}`;
}
